import { useCopyToClipboard } from "features/common/useCopyToClipboard";
import { AnimatePresence, motion } from "framer-motion";
import type { CSS, IconType } from "prism-react";
import { Button, Icon, Stack, styled } from "prism-react";
import React from "react";

import { useAnalytics } from "hooks/useAnalytics";

const IconContainer = styled(motion.div, {
  display: "flex",
  alignItems: "center",
});

const iconAnimationProps = {
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -2 },
  initial: { opacity: 0, y: 2 },
  transition: { duration: 0.15 },
};

export interface CopyButtonProps {
  content: string;
  children: React.ReactNode;
  css?: CSS;
  type: "embed" | "share";
  icon?: IconType | null;
  variant: "primary" | "secondary" | "ghost" | "ghost-inverted";
}

export const CopyButton = ({
  content,
  children,
  css,
  icon,
  type,
  variant,
}: CopyButtonProps) => {
  const [copied, setCopied] = React.useState(false);
  const track = useAnalytics();

  React.useEffect(() => {
    if (copied === true) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [copied, setCopied]);

  const copy = useCopyToClipboard(undefined, true);

  return (
    <Button
      css={css}
      onClick={() => {
        setCopied(true);
        copy(content);

        track("copy_sandbox_url", { type });
      }}
      variant={variant}
    >
      <Stack horizontal>
        {icon === null ? null : (
          <AnimatePresence initial={false} exitBeforeEnter>
            {copied ? (
              <IconContainer {...iconAnimationProps} key="check">
                <Icon size={4} type="check" />
              </IconContainer>
            ) : (
              <IconContainer {...iconAnimationProps} key="copy">
                <Icon size={4} type={icon || "hyperlink"} />
              </IconContainer>
            )}
          </AnimatePresence>
        )}

        <span>{children}</span>
      </Stack>
    </Button>
  );
};
